import React, { useRef, useEffect } from 'react';
import Editable from './Editable.jsx';
import EditableButton from './EditableButton.jsx';
import HeroBackgroundEditor from './HeroBackgroundEditor.jsx';
import BackgroundLayer from './BackgroundLayer.jsx';
import { GradientAnimator } from './GradientAnimator.jsx';
import { addUnit, normalizeAppUrl, getFontStyles, getBtnColor } from '../utils/styleHelpers.js';

export const getHeroSizeStyles = (hero, settings = {}) => {
    const sizeType = hero?.sizeType || (hero?.backgroundImage ? 'full' : 'auto');
    const vAlign = hero?.verticalAlign || 'center';
    const justifyContent = vAlign === 'top' ? 'flex-start' : (vAlign === 'bottom' ? 'flex-end' : 'center');
    const isOverlay = Boolean(settings?.headerOverlayHero);

    let base = {};
    if (sizeType === 'full') {
        base = { minHeight: '100dvh', padding: '0' };
    } else if (sizeType === 'large') {
        base = { minHeight: '80vh', padding: isOverlay ? '8rem 0 6rem' : '6rem 0' };
    } else if (sizeType === 'medium') {
        base = { minHeight: '50vh', padding: isOverlay ? '6rem 0 4rem' : '4rem 0' };
    } else if (sizeType === 'auto') {
        base = { minHeight: 'auto', padding: isOverlay ? '7rem 0 5rem' : '5rem 0' };
    } else if (sizeType === 'custom') {
        base = { minHeight: hero?.minHeight ? addUnit(hero.minHeight) : 'auto', padding: hero?.padding ? addUnit(hero.padding) : '0' };
    } else {
        base = { minHeight: 'auto', padding: isOverlay ? '7rem 0 5rem' : '5rem 0' };
    }

    if (vAlign === 'top') {
        base.paddingTop = hero?.paddingTop ? addUnit(hero.paddingTop) : (isOverlay ? '7rem' : '4rem');
        if (sizeType === 'full') base.paddingBottom = '2rem';
    } else if (vAlign === 'bottom') {
        base.paddingBottom = hero?.paddingBottom ? addUnit(hero.paddingBottom) : '5rem';
        if (isOverlay) base.paddingTop = '6rem';
    }

    return {
        ...base,
        justifyContent
    };
};

export const getHeroContainerStyle = (hero, settings) => {
    const widthMode = hero?.widthMode || 'standard';
    const align = hero?.titleAlign || hero?.textAlign || hero?.align || 'center';
    
    let maxWidth = '1200px';
    if (widthMode === 'full') {
        maxWidth = '100%';
    } else if (widthMode === 'wide') {
        maxWidth = '1600px';
    } else if (widthMode === 'standard') {
        maxWidth = settings?.containerMaxWidth ? addUnit(settings.containerMaxWidth) : '1200px';
    } else if (widthMode === 'narrow') {
        maxWidth = '860px';
    } else if (widthMode === 'compact') {
        maxWidth = '680px';
    } else if (widthMode === 'custom') {
        maxWidth = hero?.customWidth ? addUnit(hero.customWidth) : '1200px';
    }

    let padTop = hero?.contentPaddingTop !== undefined && hero?.contentPaddingTop !== '' 
        ? addUnit(hero.contentPaddingTop) 
        : (hero?.contentPaddingY !== undefined && hero?.contentPaddingY !== '' ? addUnit(hero.contentPaddingY) : '0px');
        
    let padBottom = hero?.contentPaddingBottom !== undefined && hero?.contentPaddingBottom !== '' 
        ? addUnit(hero.contentPaddingBottom) 
        : (hero?.contentPaddingY !== undefined && hero?.contentPaddingY !== '' ? addUnit(hero.contentPaddingY) : '0px');
        
    let padLeft = hero?.contentPaddingLeft !== undefined && hero?.contentPaddingLeft !== '' 
        ? addUnit(hero.contentPaddingLeft) 
        : (hero?.contentPaddingX !== undefined && hero?.contentPaddingX !== '' ? addUnit(hero.contentPaddingX) : '24px');
        
    let padRight = hero?.contentPaddingRight !== undefined && hero?.contentPaddingRight !== '' 
        ? addUnit(hero.contentPaddingRight) 
        : (hero?.contentPaddingX !== undefined && hero?.contentPaddingX !== '' ? addUnit(hero.contentPaddingX) : '24px');

    const resolvedPadding = hero?.contentPadding && hero.contentPadding !== '' 
        ? (String(hero.contentPadding).trim().includes(' ') ? hero.contentPadding : addUnit(hero.contentPadding)) 
        : `${padTop} ${padRight} ${padBottom} ${padLeft}`;

    const containerStyle = {
        position: 'relative',
        zIndex: 2,
        width: '100%',
        maxWidth: maxWidth,
        margin: '0 auto',
        marginTop: hero?.contentMarginTop ? addUnit(hero.contentMarginTop) : undefined,
        marginBottom: hero?.contentMarginBottom ? addUnit(hero.contentMarginBottom) : undefined,
        padding: resolvedPadding,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: (align === 'left' ? 'flex-start' : (align === 'right' ? 'flex-end' : (align === 'justify' ? 'stretch' : 'center'))),
        textAlign: align || 'center'
    };

    if (hero?.contentGap !== undefined && hero?.contentGap !== '') {
        containerStyle.gap = addUnit(hero.contentGap);
    }
    if (hero?.contentBg) {
        containerStyle.background = hero.contentBg;
    }
    if (hero?.contentBlur) {
        const blurVal = addUnit(hero.contentBlur);
        containerStyle.backdropFilter = `blur(${blurVal})`;
        containerStyle.WebkitBackdropFilter = `blur(${blurVal})`;
    }
    if (hero?.contentBorder) {
        containerStyle.border = hero.contentBorder;
    }
    if (hero?.contentRadius) {
        containerStyle.borderRadius = addUnit(hero.contentRadius);
    }
    if (hero?.contentShadow) {
        containerStyle.boxShadow = hero.contentShadow;
    }

    return containerStyle;
};

export const getHeroCardStyle = (hero) => {
    const cardMode = hero?.cardMode || (hero?.layoutMode === 'card' ? 'glassDark' : 'none');
    if (cardMode === 'none' && hero?.layoutMode !== 'card') return {};

    const mode = cardMode !== 'none' ? cardMode : 'glassDark';
    const cardRadius = hero?.cardRadius !== undefined && hero?.cardRadius !== '' ? hero.cardRadius : '16px';
    const cardPadding = hero?.cardPadding || '32px 28px';
    const cardBorder = hero?.cardBorder;
    const cardMaxWidth = hero?.cardMaxWidth || '720px';
    const cardShadow = hero?.cardShadow;
    const cardBlur = hero?.cardBlur;

    let baseStyle = {};
    if (mode === 'glassDark') {
        baseStyle = {
            background: hero?.cardBg || 'rgba(15, 23, 42, 0.75)',
            backdropFilter: `blur(${cardBlur || '16px'})`,
            WebkitBackdropFilter: `blur(${cardBlur || '16px'})`,
            border: cardBorder || '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: cardRadius,
            padding: cardPadding,
            boxShadow: cardShadow || '0 20px 50px rgba(0, 0, 0, 0.5)',
            maxWidth: cardMaxWidth,
            width: '100%',
            boxSizing: 'border-box'
        };
    } else if (mode === 'glassLight') {
        baseStyle = {
            background: hero?.cardBg || 'rgba(255, 255, 255, 0.85)',
            backdropFilter: `blur(${cardBlur || '16px'})`,
            WebkitBackdropFilter: `blur(${cardBlur || '16px'})`,
            border: cardBorder || '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: cardRadius,
            padding: cardPadding,
            boxShadow: cardShadow || '0 20px 50px rgba(0, 0, 0, 0.15)',
            color: '#0f172a',
            maxWidth: cardMaxWidth,
            width: '100%',
            boxSizing: 'border-box'
        };
    } else if (mode === 'solidDark') {
        baseStyle = {
            background: hero?.cardBg || '#0f172a',
            border: cardBorder || '1px solid #334155',
            borderRadius: cardRadius,
            padding: cardPadding,
            boxShadow: cardShadow || '0 20px 50px rgba(0, 0, 0, 0.6)',
            maxWidth: cardMaxWidth,
            width: '100%',
            boxSizing: 'border-box'
        };
    } else if (mode === 'solidLight') {
        baseStyle = {
            background: hero?.cardBg || '#ffffff',
            border: cardBorder || '1px solid #e2e8f0',
            borderRadius: cardRadius,
            padding: cardPadding,
            boxShadow: cardShadow || '0 20px 50px rgba(0, 0, 0, 0.1)',
            color: '#0f172a',
            maxWidth: cardMaxWidth,
            width: '100%',
            boxSizing: 'border-box'
        };
    } else if (mode === 'accentGlow') {
        baseStyle = {
            background: hero?.cardBg || 'rgba(15, 23, 42, 0.85)',
            backdropFilter: `blur(${cardBlur || '16px'})`,
            WebkitBackdropFilter: `blur(${cardBlur || '16px'})`,
            border: cardBorder || '2px solid #3b82f6',
            borderRadius: cardRadius,
            padding: cardPadding,
            boxShadow: cardShadow || '0 0 35px rgba(59, 130, 246, 0.4), 0 20px 50px rgba(0, 0, 0, 0.5)',
            maxWidth: cardMaxWidth,
            width: '100%',
            boxSizing: 'border-box'
        };
    } else if (mode === 'custom') {
        baseStyle = {
            background: hero?.cardBg || 'rgba(0, 0, 0, 0.6)',
            backdropFilter: cardBlur ? `blur(${cardBlur})` : undefined,
            WebkitBackdropFilter: cardBlur ? `blur(${cardBlur})` : undefined,
            border: cardBorder || '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: cardRadius,
            padding: cardPadding,
            boxShadow: cardShadow || '0 10px 30px rgba(0, 0, 0, 0.3)',
            maxWidth: cardMaxWidth,
            width: '100%',
            boxSizing: 'border-box'
        };
    }

    if (hero?.cardBorder) baseStyle.border = hero.cardBorder;
    if (hero?.cardShadow) baseStyle.boxShadow = hero.cardShadow;
    if (hero?.cardRadius !== undefined && hero?.cardRadius !== '') baseStyle.borderRadius = hero.cardRadius;
    if (hero?.cardPadding) baseStyle.padding = hero.cardPadding;
    if (hero?.cardBlur !== undefined && hero?.cardBlur !== '') {
        baseStyle.backdropFilter = `blur(${hero.cardBlur})`;
        baseStyle.WebkitBackdropFilter = `blur(${hero.cardBlur})`;
    }
    if (hero?.cardAlign) baseStyle.textAlign = hero.cardAlign;

    return baseStyle;
};

export const renderHeroBottomDivider = (hero, settings) => {
    const divider = hero?.bottomDivider;
    if (!divider || divider === 'none') return null;

    const defaultColor = settings?.pageBackground || '#2a0a0a';
    const color = hero?.bottomDividerColor || defaultColor;
    const height = hero?.bottomDividerHeight || '60px';
    const flip = hero?.bottomDividerFlip || false;
    const transform = flip ? 'scaleX(-1)' : 'none';

    return (
        <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            overflow: 'hidden',
            lineHeight: 0,
            pointerEvents: 'none',
            zIndex: 3,
            transform
        }}>
            {divider === 'wave' && (
                <svg viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
                    <path d="M0,32L60,42.7C120,53,240,75,360,74.7C480,75,600,53,720,48C840,43,960,53,1080,64C1200,75,1320,85,1380,90.7L1440,96L1440,120L1380,120C1320,120,1200,120,1080,120C960,120,840,120,720,120C600,120,480,120,360,120C240,120,120,120,60,120L0,120Z" fill={color} />
                </svg>
            )}
            {divider === 'slant' && (
                <svg viewBox="0 0 1440 100" fill="none" preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
                    <polygon points="0,100 1440,0 1440,100" fill={color} />
                </svg>
            )}
            {divider === 'curve' && (
                <svg viewBox="0 0 1440 100" fill="none" preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
                    <path d="M0,0 Q720,100 1440,0 L1440,100 L0,100 Z" fill={color} />
                </svg>
            )}
            {divider === 'triangle' && (
                <svg viewBox="0 0 1440 80" fill="none" preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
                    <polygon points="0,80 720,0 1440,80" fill={color} />
                </svg>
            )}
            {divider === 'fade' && (
                <div style={{ width: '100%', height, background: `linear-gradient(to bottom, transparent, ${color})` }} />
            )}
        </div>
    );
};

export const normalizeHeroTitleProp = (p) => {
    const capitalized = p.charAt(0).toUpperCase() + p.slice(1);
    if (capitalized === 'FontWeight' || capitalized === 'Weight') return ['titleFontWeight', 'titleWeight', 'fontWeight', 'weight'];
    if (capitalized === 'FontSize' || capitalized === 'Size') return ['titleSize', 'titleFontSize'];
    if (capitalized === 'TextTransform' || capitalized === 'Transform') return ['titleTextTransform', 'titleTransform'];
    if (capitalized === 'TextShadow' || capitalized === 'Shadow') return ['titleTextShadow', 'titleShadow'];
    if (capitalized === 'TextShadowPreset' || capitalized === 'ShadowPreset') return ['titleTextShadowPreset'];
    if (capitalized === 'TextShadowCustom' || capitalized === 'ShadowCustom') return ['titleTextShadowCustom'];
    return [`title${capitalized}`];
};

export const normalizeHeroSubtitleProp = (p) => {
    const capitalized = p.charAt(0).toUpperCase() + p.slice(1);
    if (capitalized === 'FontWeight' || capitalized === 'Weight') return ['subtitleFontWeight', 'subtitleWeight', 'subFontWeight', 'subWeight'];
    if (capitalized === 'FontSize' || capitalized === 'Size') return ['subtitleSize', 'subtitleFontSize'];
    if (capitalized === 'TextTransform' || capitalized === 'Transform') return ['subtitleTextTransform', 'subtitleTransform'];
    if (capitalized === 'TextShadow' || capitalized === 'Shadow') return ['subtitleTextShadow', 'subtitleShadow'];
    if (capitalized === 'TextShadowPreset' || capitalized === 'ShadowPreset') return ['subtitleTextShadowPreset'];
    if (capitalized === 'TextShadowCustom' || capitalized === 'ShadowCustom') return ['subtitleTextShadowCustom'];
    return [`subtitle${capitalized}`];
};

export const getHeroTitleStyles = (hero = {}, resolveFontStyles, alignOverride) => {
    const align = alignOverride || hero?.titleAlign || hero?.textAlign || hero?.align || 'center';
    const titleWeight = hero?.titleFontWeight || hero?.titleWeight || hero?.fontWeight || hero?.weight;
    const safeResolve = typeof resolveFontStyles === 'function' ? resolveFontStyles : ((type, o) => o);
    return {
        width: '100%',
        ...safeResolve('h1', {
            align,
            size: hero?.titleSize || hero?.titleFontSize,
            color: hero?.titleColor || hero?.textColor,
            fontFamily: hero?.titleFontFamily,
            fontWeight: titleWeight,
            weight: titleWeight,
            fontStyle: hero?.titleFontStyle,
            textDecoration: hero?.titleTextDecoration,
            letterSpacing: hero?.titleLetterSpacing,
            textTransform: hero?.titleTextTransform || hero?.titleTransform,
            lineHeight: hero?.titleLineHeight,
            textStrokeWidth: hero?.titleTextStrokeWidth,
            textStrokeColor: hero?.titleTextStrokeColor,
            customCss: hero?.titleCustomCss,
            preset: hero?.titlePreset,
            textShadow: (hero?.titleTextShadow && hero?.titleTextShadow !== 'none') ? hero?.titleTextShadow : undefined,
            textShadowPreset: hero?.titleTextShadowPreset || hero?.textShadowPreset || 'none',
            textShadowCustom: hero?.titleTextShadowCustom || hero?.textShadowCustom || '',
            textGradientMode: hero?.titleTextGradientMode,
            textGradientStart: hero?.titleTextGradientStart,
            textGradientEnd: hero?.titleTextGradientEnd,
            textGradientAngle: hero?.titleTextGradientAngle
        }),
        margin: 0,
        textAlign: align,
        ...(titleWeight ? { fontWeight: titleWeight } : {})
    };
};

export const getHeroSubtitleStyles = (hero = {}, resolveFontStyles, alignOverride) => {
    const defaultAlign = hero?.titleAlign || hero?.textAlign || hero?.align || 'center';
    const align = alignOverride || hero?.subtitleAlign || defaultAlign;
    const subWeight = hero?.subtitleFontWeight || hero?.subtitleWeight || hero?.subFontWeight || hero?.subWeight;
    const isJustify = align === 'justify';
    const safeResolve = typeof resolveFontStyles === 'function' ? resolveFontStyles : ((type, o) => o);
    return {
        width: '100%',
        ...safeResolve('h2', {
            align,
            size: hero?.subtitleSize || hero?.subtitleFontSize,
            color: hero?.subtitleColor || hero?.textColor,
            fontFamily: hero?.subtitleFontFamily,
            fontWeight: subWeight,
            weight: subWeight,
            fontStyle: hero?.subtitleFontStyle,
            textDecoration: hero?.subtitleTextDecoration,
            letterSpacing: hero?.subtitleLetterSpacing,
            textTransform: hero?.subtitleTextTransform || hero?.subtitleTransform,
            lineHeight: hero?.subtitleLineHeight,
            textStrokeWidth: hero?.subtitleTextStrokeWidth,
            textStrokeColor: hero?.subtitleTextStrokeColor,
            customCss: hero?.subtitleCustomCss,
            preset: hero?.subtitlePreset,
            textShadow: (hero?.subtitleTextShadow && hero?.subtitleTextShadow !== 'none') ? hero?.subtitleTextShadow : undefined,
            textShadowPreset: hero?.subtitleTextShadowPreset || hero?.textShadowPreset || 'none',
            textShadowCustom: hero?.subtitleTextShadowCustom || hero?.textShadowCustom || '',
            textGradientMode: hero?.subtitleTextGradientMode,
            textGradientStart: hero?.subtitleTextGradientStart,
            textGradientEnd: hero?.subtitleTextGradientEnd,
            textGradientAngle: hero?.subtitleTextGradientAngle
        }),
        ...(subWeight ? { fontWeight: subWeight } : {}),
        margin: 0,
        maxWidth: isJustify ? (hero?.subtitleMaxWidth || '100%') : (hero?.subtitleMaxWidth || '800px'),
        marginLeft: (align === 'left' || isJustify) ? '0' : (align === 'right' ? 'auto' : 'auto'),
        marginRight: align === 'left' ? 'auto' : (align === 'right' || isJustify ? '0' : 'auto'),
        textAlign: align
    };
};

export const HeroBlock = ({
    hero = {},
    pageIndex,
    heroPathPrefix,
    update,
    settings = {},
    isEditing = false,
    getFontStyles: _propGetFontStyles,
    getBtnColor: _propGetBtnColor,
    imgStyleBase = {},
    btnStyleBase = {}
}) => {
    // Resolve font styles & button colors with safe fallbacks
    const resolveFontStyles = typeof _propGetFontStyles === 'function' ? _propGetFontStyles : getFontStyles(settings?.typography || {});
    const _getFontStyles = resolveFontStyles;
    const resolveBtnColor = typeof _propGetBtnColor === 'function' ? _propGetBtnColor : getBtnColor(settings);
    const _getBtnColor = resolveBtnColor;

    const pathPrefix = heroPathPrefix || ((pageIndex !== undefined && pageIndex !== null && pageIndex !== -1) ? ['pages', pageIndex, 'hero'] : ['hero']);

    const getHeroTitleStylesLocal = (alignOverride) => getHeroTitleStyles(hero, resolveFontStyles, alignOverride);
    const getHeroSubtitleStylesLocal = (alignOverride) => getHeroSubtitleStyles(hero, resolveFontStyles, alignOverride);

    const headerRef = useRef(null);

    const cardParallaxEnabled = Boolean(hero?.heroCardParallax);
    const cardParallaxTarget = hero?.heroCardParallaxTarget || 'card_and_text';
    const cardParallaxSpeed = parseFloat(hero?.heroCardParallaxSpeed ?? 0.35) || 0.35;
    const cardParallaxMaxTravel = parseFloat(hero?.heroCardParallaxMaxTravel ?? 90) || 90;
    const cardParallaxDirection = hero?.heroCardParallaxDirection || 'up';
    const cardParallaxSmoothing = hero?.heroCardParallaxSmoothing || 'cubic-bezier(0.16, 1, 0.3, 1)';
    const cardParallaxFade = hero?.heroCardParallaxFade || 'none';
    const cardParallaxScale = hero?.heroCardParallaxScale || 'none';
    const cardParallaxTilt = hero?.heroCardParallaxTilt || 'none';
    const cardParallaxMobile = hero?.heroCardParallaxMobile || 'reduced';

    useEffect(() => {
        const headerEl = headerRef.current;
        if (!headerEl) return;

        const clearTransforms = () => {
            const nodes = headerEl.querySelectorAll('.hero-content-inner, .hero-split-layout, .hero-title, .hero-subtitle, .hero-buttons-container, .hero-foreground-image, .hero-badge-pill');
            nodes.forEach(n => {
                n.style.transform = '';
                n.style.opacity = '';
                n.style.willChange = '';
            });
        };

        if (!cardParallaxEnabled) {
            clearTransforms();
            return;
        }

        const EASE_MAP = {
            'cubic-bezier(0.16, 1, 0.3, 1)': 0.09,
            'ease-out': 0.15,
            'ease-in-out': 0.07,
            'linear': 0.35,
            'cubic-bezier(0.34, 1.56, 0.64, 1)': 0.06,
            'cubic-bezier(0.68, -0.6, 0.32, 1.6)': 0.04
        };
        const easeFactor = EASE_MAP[cardParallaxSmoothing] || 0.09;

        let rafId = null;
        let currentProgress = 0;
        let targetProgress = 0;
        let isAnimating = false;

        const buildTransform = (prog, layerMult = 1) => {
            const isMobile = window.innerWidth <= 768;
            if (isMobile && cardParallaxMobile === 'disabled') {
                return { transform: '', opacity: '' };
            }
            const mobileMult = (isMobile && cardParallaxMobile === 'reduced') ? 0.5 : 1;
            const dist = prog * cardParallaxMaxTravel * (cardParallaxSpeed / 0.35) * layerMult * mobileMult;

            let tx = 0;
            let ty = 0;
            if (cardParallaxDirection === 'up') ty = -dist;
            else if (cardParallaxDirection === 'down') ty = dist;
            else if (cardParallaxDirection === 'left') tx = -dist;
            else if (cardParallaxDirection === 'right') tx = dist;
            else if (cardParallaxDirection === 'diagonal-up-right') { tx = dist * 0.7; ty = -dist * 0.7; }
            else if (cardParallaxDirection === 'diagonal-up-left') { tx = -dist * 0.7; ty = -dist * 0.7; }
            else ty = -dist;

            const parts = [`translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0)`];

            if (cardParallaxScale === 'zoom-in') {
                const sc = 1 + Math.max(-0.08, Math.min(0.12, prog * 0.08 * layerMult));
                parts.push(`scale(${sc.toFixed(3)})`);
            } else if (cardParallaxScale === 'zoom-out') {
                const sc = 1 - Math.max(-0.08, Math.min(0.12, prog * 0.08 * layerMult));
                parts.push(`scale(${sc.toFixed(3)})`);
            }

            if (cardParallaxTilt === 'tilt-back') {
                const deg = Math.max(-18, Math.min(18, prog * 12 * layerMult));
                parts.unshift('perspective(900px)');
                parts.push(`rotateX(${deg.toFixed(2)}deg)`);
            } else if (cardParallaxTilt === 'tilt-forward') {
                const deg = Math.max(-18, Math.min(18, -prog * 12 * layerMult));
                parts.unshift('perspective(900px)');
                parts.push(`rotateX(${deg.toFixed(2)}deg)`);
            }

            let op = '';
            if (cardParallaxFade === 'fade-out') {
                op = String(Math.max(0, Math.min(1, 1 - Math.max(0, prog) * 1.15)).toFixed(3));
            } else if (cardParallaxFade === 'fade-edges') {
                op = String(Math.max(0, Math.min(1, 1 - Math.abs(prog) * 1.1)).toFixed(3));
            }

            return { transform: parts.join(' '), opacity: op };
        };

        const applyFrame = (prog) => {
            if (cardParallaxTarget === 'card_and_text') {
                const targets = headerEl.querySelectorAll('.hero-content-inner, .hero-split-layout');
                const { transform, opacity } = buildTransform(prog, 1);
                targets.forEach(el => {
                    el.style.willChange = 'transform, opacity';
                    el.style.transform = transform;
                    if (opacity !== '') el.style.opacity = opacity;
                    else el.style.opacity = '';
                });
            } else if (cardParallaxTarget === 'text_only') {
                const targets = headerEl.querySelectorAll('.hero-badge-pill, .hero-title, .hero-subtitle, .hero-buttons-container, .hero-foreground-image');
                const { transform, opacity } = buildTransform(prog, 1);
                targets.forEach(el => {
                    el.style.willChange = 'transform, opacity';
                    el.style.transform = transform;
                    if (opacity !== '') el.style.opacity = opacity;
                    else el.style.opacity = '';
                });
            } else if (cardParallaxTarget === 'layered') {
                const layerSelectors = [
                    { sel: '.hero-badge-pill', mult: 0.65 },
                    { sel: '.hero-foreground-image', mult: 0.8 },
                    { sel: '.hero-title', mult: 1.0 },
                    { sel: '.hero-subtitle', mult: 1.25 },
                    { sel: '.hero-buttons-container', mult: 1.5 }
                ];
                layerSelectors.forEach(({ sel, mult }) => {
                    const els = headerEl.querySelectorAll(sel);
                    const { transform, opacity } = buildTransform(prog, mult);
                    els.forEach(el => {
                        el.style.willChange = 'transform, opacity';
                        el.style.transform = transform;
                        if (opacity !== '') el.style.opacity = opacity;
                        else el.style.opacity = '';
                    });
                });
            }
        };

        const animate = () => {
            currentProgress += (targetProgress - currentProgress) * easeFactor;
            if (Math.abs(targetProgress - currentProgress) > 0.001) {
                applyFrame(currentProgress);
                rafId = requestAnimationFrame(animate);
            } else {
                currentProgress = targetProgress;
                applyFrame(targetProgress);
                isAnimating = false;
            }
        };

        const handleScroll = () => {
            if (!headerEl) return;
            const rect = headerEl.getBoundingClientRect();
            const span = Math.max(window.innerHeight * 0.85, rect.height || 500);
            targetProgress = Math.max(-1, Math.min(1.5, -rect.top / span));
            if (!isAnimating) {
                isAnimating = true;
                rafId = requestAnimationFrame(animate);
            }
        };

        clearTransforms();
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll, { passive: true });
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
            if (rafId) cancelAnimationFrame(rafId);
            clearTransforms();
        };
    }, [
        cardParallaxEnabled,
        cardParallaxTarget,
        cardParallaxSpeed,
        cardParallaxMaxTravel,
        cardParallaxDirection,
        cardParallaxSmoothing,
        cardParallaxFade,
        cardParallaxScale,
        cardParallaxTilt,
        cardParallaxMobile
    ]);

    if (hero.hideHero && !isEditing) return null;

    if (hero.hideHero && isEditing) {
        return (
            <div style={{
                padding: '20px',
                textAlign: 'center',
                background: 'rgba(0,0,0,0.25)',
                border: '1.5px dashed rgba(255,255,255,0.25)',
                borderRadius: '8px',
                margin: '20px auto',
                maxWidth: '600px',
                color: '#94a3b8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
            }}>
                <span>🙈 <strong>Hero is Hidden</strong> (Visible only to visitors when enabled)</span>
                <button
                    type="button"
                    onClick={() => update([...pathPrefix, 'hideHero'], false)}
                    style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 16px', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem' }}
                >
                    👁 Show Hero
                </button>
                <HeroBackgroundEditor hero={hero} onChange={(field, val) => update([...pathPrefix, field], val)} settings={settings} onChangeSetting={(key, val) => update(['settings', key], val)} isEditing={isEditing} />
            </div>
        );
    }

    const heroId = `hero-block-${pageIndex !== undefined ? pageIndex : 'main'}`;
    const buttonsDirection = hero.buttonsDirection || hero.buttonsLayout || hero.buttonsFlexDirection || 'row';
    const isButtonsColumn = buttonsDirection === 'column' || buttonsDirection === 'vertical';

    const getMobileCss = () => {
        const rules = [];

        if (hero.mobileMinHeight) {
            rules.push(`#${heroId} { min-height: ${addUnit(hero.mobileMinHeight)} !important; }`);
        }
        if (hero.mobilePadding) {
            rules.push(`#${heroId} { padding: ${hero.mobilePadding} !important; }`);
        }

        if (hero.mobileAlign) {
            const mAlign = hero.mobileAlign;
            const mFlexAlign = mAlign === 'left' ? 'flex-start' : (mAlign === 'right' ? 'flex-end' : (mAlign === 'justify' ? 'stretch' : 'center'));
            rules.push(`
                #${heroId} {
                    align-items: ${mFlexAlign} !important;
                    text-align: ${mAlign} !important;
                }
                #${heroId} .hero-main-container,
                #${heroId} .hero-content-inner {
                    align-items: ${mFlexAlign} !important;
                    text-align: ${mAlign} !important;
                }
                #${heroId} .hero-title {
                    text-align: ${mAlign} !important;
                }
                #${heroId} .hero-subtitle {
                    text-align: ${mAlign} !important;
                    margin-left: ${(mAlign === 'left' || mAlign === 'justify') ? '0' : (mAlign === 'right' ? 'auto' : 'auto')} !important;
                    margin-right: ${mAlign === 'left' ? 'auto' : (mAlign === 'right' || mAlign === 'justify' ? '0' : 'auto')} !important;
                }
            `);
        }

        if (hero.mobileTitleSize) {
            rules.push(`#${heroId} .hero-title, #${heroId} h1 { font-size: ${addUnit(hero.mobileTitleSize)} !important; }`);
        }
        if (hero.mobileTitleLineHeight) {
            rules.push(`#${heroId} .hero-title, #${heroId} h1 { line-height: ${hero.mobileTitleLineHeight} !important; }`);
        }

        if (hero.mobileSubtitleSize) {
            rules.push(`#${heroId} .hero-subtitle, #${heroId} p.hero-subtitle { font-size: ${addUnit(hero.mobileSubtitleSize)} !important; }`);
        }
        if (hero.mobileSubtitleLineHeight) {
            rules.push(`#${heroId} .hero-subtitle, #${heroId} p.hero-subtitle { line-height: ${hero.mobileSubtitleLineHeight} !important; }`);
        }

        const btnLayout = hero.mobileButtonLayout || (isButtonsColumn ? 'stack' : 'inline');
        if (btnLayout === 'stack') {
            const gap = addUnit(hero.mobileButtonGap || '12px');
            rules.push(`
                #${heroId} .hero-buttons-container {
                    display: flex !important;
                    flex-direction: column !important;
                    width: 100% !important;
                    align-items: stretch !important;
                    justify-content: stretch !important;
                    gap: ${gap} !important;
                }
                #${heroId} .hero-buttons-container > * {
                    width: 100% !important;
                    display: block !important;
                    box-sizing: border-box !important;
                }
                #${heroId} .hero-buttons-container a,
                #${heroId} .hero-buttons-container button {
                    width: 100% !important;
                    justify-content: center !important;
                    text-align: center !important;
                    box-sizing: border-box !important;
                }
            `);
        } else if (btnLayout === 'center') {
            const gap = addUnit(hero.mobileButtonGap || '10px');
            rules.push(`
                #${heroId} .hero-buttons-container {
                    display: flex !important;
                    flex-direction: row !important;
                    flex-wrap: wrap !important;
                    justify-content: center !important;
                    align-items: center !important;
                    gap: ${gap} !important;
                }
            `);
        } else if (btnLayout === 'inline') {
            const gap = addUnit(hero.mobileButtonGap || '8px');
            const mAlign = hero.mobileAlign || hero.titleAlign || 'center';
            const justify = mAlign === 'left' ? 'flex-start' : (mAlign === 'right' ? 'flex-end' : 'center');
            rules.push(`
                #${heroId} .hero-buttons-container {
                    display: flex !important;
                    flex-direction: row !important;
                    flex-wrap: wrap !important;
                    justify-content: ${justify} !important;
                    align-items: center !important;
                    gap: ${gap} !important;
                }
            `);
        }

        if (hero.mobileButtonPadding) {
            rules.push(`
                #${heroId} .hero-buttons-container a,
                #${heroId} .hero-buttons-container button {
                    padding: ${hero.mobileButtonPadding} !important;
                }
            `);
        }

        if (hero.mobileCardPadding) {
            rules.push(`
                #${heroId} .hero-content-card {
                    padding: ${hero.mobileCardPadding} !important;
                }
            `);
        }

        if (hero.mobileHideCardBg) {
            rules.push(`
                #${heroId} .hero-content-card {
                    background: transparent !important;
                    border-color: transparent !important;
                    box-shadow: none !important;
                    backdrop-filter: none !important;
                    -webkit-backdrop-filter: none !important;
                }
            `);
        }

        if (hero.mobileBgPosition) {
            rules.push(`
                #${heroId} [style*="background-image"],
                #${heroId} video {
                    background-position: ${hero.mobileBgPosition} !important;
                    object-position: ${hero.mobileBgPosition} !important;
                }
            `);
        }

        if (hero.mobileHideMedia) {
            rules.push(`
                #${heroId} .hero-foreground-image {
                    display: none !important;
                }
            `);
        }

        rules.push(`
            #${heroId} .hero-split-layout {
                flex-direction: column !important;
                gap: 24px !important;
            }
            #${heroId} .hero-split-layout > div {
                width: 100% !important;
                flex: 1 1 100% !important;
            }
        `);

        if (rules.length === 0) return '';
        return `@media (max-width: 768px) { ${rules.join('\n')} }`;
    };

    const mobileCss = getMobileCss();

    return (
        <header
            ref={headerRef}
            id={heroId}
            className="hero-block-wrapper"
            style={{
                position: 'relative',
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                alignItems: (hero.titleAlign === 'left' ? 'flex-start' : (hero.titleAlign === 'right' ? 'flex-end' : (hero.titleAlign === 'justify' ? 'stretch' : 'center'))),
                justifyContent: 'center',
                textAlign: hero.titleAlign || 'center',
                color: hero.textColor || '#ffffff',
                width: '100%',
                ...getHeroSizeStyles(hero, settings)
            }}
        >
            {mobileCss && <style dangerouslySetInnerHTML={{ __html: mobileCss }} />}
            <HeroBackgroundEditor hero={hero} onChange={(field, val) => update([...pathPrefix, field], val)} settings={settings} onChangeSetting={(key, val) => update(['settings', key], val)} isEditing={isEditing} />
            
            {/* Scroll-animated gradient for hero */}
            {(() => {
                const effHeroBgMode = hero?.heroBgMode || hero?.bgMode || settings?.heroBgMode || 'solid';
                const effHeroGradientAnimate = effHeroBgMode === 'gradient' && Boolean(hero?.heroGradientAnimate !== undefined ? (hero?.heroGradientAnimate || hero?.gradientAnimate) : settings?.heroGradientAnimate);
                const getHeroBgGradientStyle = (h, s) => {
                    const start = h?.heroGradientStart || h?.gradientStart || s?.heroGradientStart || '#2a0a0a';
                    const mid = h?.heroGradientMid !== undefined ? (h?.heroGradientMid || h?.gradientMid) : s?.heroGradientMid;
                    const end = h?.heroGradientEnd || h?.gradientEnd || s?.heroGradientEnd || '#000000';
                    const angle = h?.heroGradientAngle || h?.gradientAngle || s?.heroGradientAngle || '135';
                    const stops = mid ? `${start}, ${mid}, ${end}` : `${start}, ${end}`;
                    return `linear-gradient(${angle}deg, ${stops})`;
                };
                return (
                    <>
                        {effHeroGradientAnimate && (
                            <GradientAnimator prefix="hero" settings={settings} customObj={hero} fixed={false} />
                        )}
                        <BackgroundLayer
                            bgImage={effHeroBgMode === 'gradient' ? undefined : hero.backgroundImage}
                            backgroundLoop={hero.backgroundLoop !== false}
                            bgColor={effHeroBgMode === 'gradient' && !effHeroGradientAnimate
                                ? getHeroBgGradientStyle(hero, settings)
                                : (effHeroBgMode === 'gradient' && effHeroGradientAnimate ? 'transparent' : hero.backgroundColor)}
                            overlayOpacity={hero.overlayOpacity}
                            overlayColor={hero.overlayColor}
                            overlayAlways={hero.overlayAlways}
                            overlayType={hero.overlayType}
                            overlayGradientAngle={hero.overlayGradientAngle}
                            overlayGradientStart={hero.overlayGradientStart}
                            overlayGradientMid={hero.overlayGradientMid}
                            overlayGradientEnd={hero.overlayGradientEnd}
                            overlayGradientStartOpacity={hero.overlayGradientStartOpacity}
                            overlayGradientMidOpacity={hero.overlayGradientMidOpacity}
                            overlayGradientEndOpacity={hero.overlayGradientEndOpacity}
                            overlayGradientStartPos={hero.overlayGradientStartPos}
                            overlayGradientMidPos={hero.overlayGradientMidPos}
                            overlayGradientEndPos={hero.overlayGradientEndPos}
                            overlayGradientAnimate={hero.overlayGradientAnimate}
                            overlayGradientAnimateMode={hero.overlayGradientAnimateMode}
                            overlayGradientAnimateSpeed={hero.overlayGradientAnimateSpeed}
                            bgSize={hero.backgroundSize}
                            bgPosition={hero.backgroundPosition}
                            bgRepeat={hero.backgroundRepeat}
                            bgDesaturate={hero.backgroundDesaturate !== undefined ? hero.backgroundDesaturate : (hero.bgDesaturate !== undefined ? hero.bgDesaturate : (settings.heroBgDesaturate !== undefined ? settings.heroBgDesaturate : 0))}
                            bgFilter={hero.backgroundFilter || hero.bgFilter || settings.heroBgFilter}
                            bgTint={hero.backgroundTint || hero.bgTint || settings.heroBgTint}
                            bgTintOpacity={hero.backgroundTintOpacity !== undefined ? hero.backgroundTintOpacity : (hero.bgTintOpacity !== undefined ? hero.bgTintOpacity : settings.heroBgTintOpacity)}
                            bgTintBlendMode={hero.backgroundTintBlendMode || hero.bgTintBlendMode || settings.heroBgTintBlendMode || 'multiply'}
                            bgHoverInteraction={hero.bgHoverInteraction || hero.backgroundHoverInteraction || settings.heroBgHoverInteraction || 'none'}
                            bgClickInteraction={hero.bgClickInteraction || hero.backgroundClickInteraction || settings.heroBgClickInteraction || 'none'}
                            bgTransitionDuration={hero.bgTransitionDuration || hero.backgroundTransitionDuration || settings.heroBgTransitionDuration || '0.5s'}
                            bgTransitionTiming={hero.bgTransitionTiming || hero.backgroundTransitionTiming || settings.heroBgTransitionTiming || 'cubic-bezier(0.4, 0, 0.2, 1)'}
                            overlayBlendMode={hero.overlayBlendMode || settings.heroOverlayBlendMode}
                            parallax={hero.backgroundParallax}
                            parallaxSpeed={hero.backgroundParallaxSpeed}
                            parallaxSmoothing={hero.backgroundParallaxSmoothing}
                            parallaxZoomMode={hero.backgroundParallaxZoomMode}
                        />
                    </>
                );
            })()}

            {/* Foreground image: free-position mode */}
            {hero.foregroundImage && hero.imageFreePosition && (
                <Editable
                    type="image"
                    as="img"
                    className="hero-foreground-image"
                    isEditing={isEditing}
                    value={hero.foregroundImage}
                    onChange={e => update([...pathPrefix, 'foregroundImage'], e.target.value)}
                    style={{
                        ...imgStyleBase,
                        position: 'absolute',
                        top: hero.imageTop !== undefined && hero.imageTop !== '' ? hero.imageTop : '0px',
                        left: hero.imageLeft !== undefined && hero.imageLeft !== '' ? hero.imageLeft : '0px',
                        right: hero.imageRight !== undefined && hero.imageRight !== '' ? hero.imageRight : 'auto',
                        bottom: hero.imageBottom !== undefined && hero.imageBottom !== '' ? hero.imageBottom : 'auto',
                        zIndex: hero.imageZIndex !== undefined && hero.imageZIndex !== '' ? hero.imageZIndex : 1,
                        width: hero.imageWidth || '300px',
                        maxWidth: hero.imageMaxWidth || '100vw',
                        margin: 0,
                        boxShadow: hero.imageShadowColor
                            ? `${hero.imageShadowX || '0px'} ${hero.imageShadowY || '4px'} ${hero.imageShadowBlur || '10px'} ${hero.imageShadowSpread || '0px'} ${hero.imageShadowColor}`
                            : (imgStyleBase?.boxShadow || 'none'),
                    }}
                />
            )}

            {/* Hero Container */}
            <div className="hero-main-container" style={getHeroContainerStyle(hero, settings)}>
                {(() => {
                    const layoutMode = hero.layoutMode || 'stack';
                    const cardStyle = getHeroCardStyle(hero);
                    const hasCard = Object.keys(cardStyle).length > 0;
                    const heroAlign = hero.titleAlign || hero.textAlign || hero.align || 'center';
                    const heroSubAlign = hero.subtitleAlign || heroAlign;
                    const heroGap = addUnit(hero.contentGap !== undefined && hero.contentGap !== '' ? hero.contentGap : '20px');

                    const handleTitleStyleChange = (p, v) => {
                        const keys = normalizeHeroTitleProp(p);
                        keys.forEach(k => update([...pathPrefix, k], v));
                    };

                    const handleSubtitleStyleChange = (p, v) => {
                        const keys = normalizeHeroSubtitleProp(p);
                        keys.forEach(k => update([...pathPrefix, k], v));
                    };

                    const renderBadge = () => (hero.badgeText || (isEditing && hero.showBadge)) ? (
                        <div className="hero-badge-pill" style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: hero.badgeBg || 'rgba(59, 130, 246, 0.15)',
                            color: hero.badgeColor || '#60a5fa',
                            border: hero.badgeBorder || '1px solid rgba(59, 130, 246, 0.3)',
                            borderRadius: hero.badgeRadius || '9999px',
                            padding: '6px 14px',
                            fontSize: hero.badgeFontSize || '13px',
                            fontWeight: '600',
                            letterSpacing: '0.05em',
                            marginBottom: '16px',
                            maxWidth: 'fit-content'
                        }}>
                            <Editable
                                as="span"
                                isEditing={isEditing}
                                value={hero.badgeText || '✨ NEW ARRIVAL'}
                                onChange={e => update([...pathPrefix, 'badgeText'], e.target.value)}
                                style={{ color: 'inherit', fontWeight: 'inherit', letterSpacing: 'inherit' }}
                            />
                        </div>
                    ) : null;

                    const renderImage = () => hero.foregroundImage && !hero.imageFreePosition ? (
                        <Editable
                            type="image"
                            as="img"
                            className="hero-foreground-image"
                            isEditing={isEditing}
                            value={hero.foregroundImage}
                            onChange={e => update([...pathPrefix, 'foregroundImage'], e.target.value)}
                            style={{
                                ...imgStyleBase,
                                width: hero.imageWidth || '350px',
                                maxWidth: hero.imageMaxWidth || '100%',
                                borderRadius: hero.imageRadius !== undefined && hero.imageRadius !== '' ? hero.imageRadius : (imgStyleBase?.borderRadius || '0px'),
                                marginTop: hero.imageMarginTop || '0px',
                                marginBottom: hero.imageMarginBottom || '20px',
                                marginLeft: (!hero.imageAlign || hero.imageAlign === 'center') ? 'auto' : (hero.imageAlign === 'right' ? 'auto' : '0'),
                                marginRight: (!hero.imageAlign || hero.imageAlign === 'center') ? 'auto' : (hero.imageAlign === 'left' ? 'auto' : '0'),
                                display: 'block',
                                boxShadow: hero.imageShadowColor
                                    ? `${hero.imageShadowX || '0px'} ${hero.imageShadowY || '4px'} ${hero.imageShadowBlur || '10px'} ${hero.imageShadowSpread || '0px'} ${hero.imageShadowColor}`
                                    : (imgStyleBase?.boxShadow || 'none'),
                            }}
                        />
                    ) : null;

                    const renderContent = (includeImage = true) => (
                        <div
                            className={`hero-content-inner ${hasCard ? 'hero-content-card' : ''}`.trim()}
                            style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: (heroAlign === 'left' ? 'flex-start' : (heroAlign === 'right' ? 'flex-end' : (heroAlign === 'justify' ? 'stretch' : 'center'))),
                                textAlign: heroAlign,
                                gap: heroGap,
                                ...(hasCard ? cardStyle : {})
                            }}
                        >
                            {includeImage && hero.imagePosition === 'top' && renderImage()}
                            {renderBadge()}
                            {(isEditing || hero.title) && (
                                <Editable
                                    as="h1"
                                    className="hero-title"
                                    isEditing={isEditing}
                                    value={hero.title || ''}
                                    onChange={e => update([...pathPrefix, 'title'], e.target.value)}
                                    onStyleChange={handleTitleStyleChange}
                                    currentStyles={{
                                        align: heroAlign,
                                        size: hero.titleSize,
                                        color: hero.titleColor,
                                        fontFamily: hero.titleFontFamily,
                                        preset: hero.titlePreset,
                                        fontWeight: hero.titleFontWeight || hero.titleWeight || hero.fontWeight || hero.weight,
                                        weight: hero.titleFontWeight || hero.titleWeight || hero.fontWeight || hero.weight,
                                        fontStyle: hero.titleFontStyle,
                                        textDecoration: hero.titleTextDecoration,
                                        textTransform: hero.titleTextTransform || hero.titleTransform,
                                        letterSpacing: hero.titleLetterSpacing,
                                        lineHeight: hero.titleLineHeight,
                                        textStrokeWidth: hero.titleTextStrokeWidth,
                                        textStrokeColor: hero.titleTextStrokeColor,
                                        textShadow: (hero.titleTextShadow && hero.titleTextShadow !== 'none') ? hero.titleTextShadow : undefined,
                                        textShadowPreset: hero.titleTextShadowPreset || hero.textShadowPreset || 'none',
                                        textShadowCustom: hero.titleTextShadowCustom || hero.textShadowCustom || '',
                                        textGradientMode: hero.titleTextGradientMode,
                                        textGradientStart: hero.titleTextGradientStart,
                                        textGradientEnd: hero.titleTextGradientEnd,
                                        textGradientAngle: hero.titleTextGradientAngle
                                    }}
                                    style={getHeroTitleStylesLocal(heroAlign)}
                                />
                            )}
                            {(isEditing || hero.subtitle) && (
                                <Editable
                                    as="p"
                                    className="hero-subtitle"
                                    isEditing={isEditing}
                                    value={hero.subtitle || ''}
                                    onChange={e => update([...pathPrefix, 'subtitle'], e.target.value)}
                                    onStyleChange={handleSubtitleStyleChange}
                                    currentStyles={{
                                        align: heroSubAlign,
                                        size: hero.subtitleSize,
                                        color: hero.subtitleColor,
                                        fontFamily: hero.subtitleFontFamily,
                                        preset: hero.subtitlePreset,
                                        fontWeight: hero.subtitleFontWeight || hero.subtitleWeight || hero.subFontWeight || hero.subWeight,
                                        weight: hero.subtitleFontWeight || hero.subtitleWeight || hero.subFontWeight || hero.subWeight,
                                        fontStyle: hero.subtitleFontStyle,
                                        textDecoration: hero.subtitleTextDecoration,
                                        textTransform: hero.subtitleTextTransform || hero.subtitleTransform,
                                        letterSpacing: hero.subtitleLetterSpacing,
                                        lineHeight: hero.subtitleLineHeight,
                                        textStrokeWidth: hero.subtitleTextStrokeWidth,
                                        textStrokeColor: hero.subtitleTextStrokeColor,
                                        textShadow: (hero.subtitleTextShadow && hero.subtitleTextShadow !== 'none') ? hero.subtitleTextShadow : undefined,
                                        textShadowPreset: hero.subtitleTextShadowPreset || hero.textShadowPreset || 'none',
                                        textShadowCustom: hero.subtitleTextShadowCustom || hero.textShadowCustom || '',
                                        textGradientMode: hero.subtitleTextGradientMode,
                                        textGradientStart: hero.subtitleTextGradientStart,
                                        textGradientEnd: hero.subtitleTextGradientEnd,
                                        textGradientAngle: hero.subtitleTextGradientAngle
                                    }}
                                    style={getHeroSubtitleStylesLocal(heroSubAlign)}
                                />
                            )}
                            {includeImage && (!hero.imagePosition || hero.imagePosition === 'middle') && renderImage()}
                            {((hero.buttons && hero.buttons.length > 0) || isEditing) && (
                                <div
                                    className="hero-buttons-container"
                                    style={{
                                        display: 'flex',
                                        flexDirection: isButtonsColumn ? 'column' : 'row',
                                        gap: addUnit(hero.buttonsGap || '15px'),
                                        justifyContent: (hero.buttonsAlign && hero.buttonsAlign !== 'inherit')
                                            ? (hero.buttonsAlign === 'left' ? 'flex-start' : hero.buttonsAlign === 'right' ? 'flex-end' : 'center')
                                            : (heroAlign === 'left' ? 'flex-start' : (heroAlign === 'right' ? 'flex-end' : 'center')),
                                        flexWrap: 'wrap',
                                        alignItems: isButtonsColumn ? (hero.buttonsAlign === 'left' ? 'flex-start' : hero.buttonsAlign === 'right' ? 'flex-end' : 'center') : 'center',
                                        width: isButtonsColumn ? '100%' : 'auto',
                                        maxWidth: '100%'
                                    }}
                                >
                                    {(hero.buttons || []).map((btn, i) => (
                                        <EditableButton
                                            key={i}
                                            button={btn}
                                            isEditing={isEditing}
                                            onChange={(newBtn) => update([...pathPrefix, 'buttons', i], newBtn)}
                                            onDelete={() => {
                                                const newButtons = hero.buttons.filter((_, idx) => idx !== i);
                                                update([...pathPrefix, 'buttons'], newButtons);
                                            }}
                                            onApplyToAll={(styledBtn) => {
                                                const updatedButtons = (hero.buttons || []).map(b => ({
                                                    ...b,
                                                    backgroundColor: styledBtn.backgroundColor !== undefined ? styledBtn.backgroundColor : b.backgroundColor,
                                                    textColor: styledBtn.textColor !== undefined ? styledBtn.textColor : b.textColor,
                                                    borderRadius: styledBtn.borderRadius !== undefined ? styledBtn.borderRadius : b.borderRadius,
                                                    padding: styledBtn.padding !== undefined ? styledBtn.padding : b.padding,
                                                    fontSize: styledBtn.fontSize !== undefined ? styledBtn.fontSize : b.fontSize,
                                                    fontWeight: styledBtn.fontWeight !== undefined ? styledBtn.fontWeight : b.fontWeight,
                                                    textTransform: styledBtn.textTransform !== undefined ? styledBtn.textTransform : b.textTransform,
                                                    letterSpacing: styledBtn.letterSpacing !== undefined ? styledBtn.letterSpacing : b.letterSpacing,
                                                    width: styledBtn.width !== undefined ? styledBtn.width : b.width,
                                                    border: styledBtn.border !== undefined ? styledBtn.border : b.border,
                                                    borderWidth: styledBtn.borderWidth !== undefined ? styledBtn.borderWidth : b.borderWidth,
                                                    borderColor: styledBtn.borderColor !== undefined ? styledBtn.borderColor : b.borderColor,
                                                    borderStyle: styledBtn.borderStyle !== undefined ? styledBtn.borderStyle : b.borderStyle,
                                                    boxShadow: styledBtn.boxShadow !== undefined ? styledBtn.boxShadow : b.boxShadow,
                                                    hoverEffect: styledBtn.hoverEffect !== undefined ? styledBtn.hoverEffect : b.hoverEffect,
                                                    hoverBackgroundColor: styledBtn.hoverBackgroundColor !== undefined ? styledBtn.hoverBackgroundColor : b.hoverBackgroundColor,
                                                    hoverTextColor: styledBtn.hoverTextColor !== undefined ? styledBtn.hoverTextColor : b.hoverTextColor,
                                                    glowColor: styledBtn.glowColor !== undefined ? styledBtn.glowColor : b.glowColor,
                                                    variant: styledBtn.variant !== undefined ? styledBtn.variant : b.variant
                                                }));
                                                update(pathPrefix, {
                                                    ...hero,
                                                    buttons: updatedButtons,
                                                    ...(styledBtn.backgroundColor !== undefined ? { heroButtonBg: styledBtn.backgroundColor } : {}),
                                                    ...(styledBtn.textColor !== undefined ? { heroButtonColor: styledBtn.textColor } : {}),
                                                    ...(styledBtn.borderRadius !== undefined ? { heroButtonRadius: styledBtn.borderRadius } : {}),
                                                    ...(styledBtn.padding !== undefined ? { heroButtonPadding: styledBtn.padding } : {}),
                                                    ...(styledBtn.fontSize !== undefined ? { heroButtonFontSize: styledBtn.fontSize } : {}),
                                                    ...(styledBtn.fontWeight !== undefined ? { heroButtonFontWeight: styledBtn.fontWeight } : {}),
                                                    ...(styledBtn.textTransform !== undefined ? { heroButtonTextTransform: styledBtn.textTransform } : {}),
                                                    ...(styledBtn.letterSpacing !== undefined ? { heroButtonLetterSpacing: styledBtn.letterSpacing } : {}),
                                                    ...(styledBtn.width !== undefined ? { heroButtonWidth: styledBtn.width } : {}),
                                                    ...(styledBtn.border !== undefined ? { heroButtonBorder: styledBtn.border } : {}),
                                                    ...(styledBtn.borderWidth !== undefined ? { heroButtonBorderWidth: styledBtn.borderWidth } : {}),
                                                    ...(styledBtn.borderColor !== undefined ? { heroButtonBorderColor: styledBtn.borderColor } : {}),
                                                    ...(styledBtn.borderStyle !== undefined ? { heroButtonBorderStyle: styledBtn.borderStyle } : {}),
                                                    ...(styledBtn.boxShadow !== undefined ? { heroButtonBoxShadow: styledBtn.boxShadow } : {}),
                                                    ...(styledBtn.hoverEffect !== undefined ? { heroButtonHoverEffect: styledBtn.hoverEffect } : {}),
                                                    ...(styledBtn.hoverBackgroundColor !== undefined ? { heroButtonHoverBg: styledBtn.hoverBackgroundColor } : {}),
                                                    ...(styledBtn.hoverTextColor !== undefined ? { heroButtonHoverColor: styledBtn.hoverTextColor } : {}),
                                                    ...(styledBtn.glowColor !== undefined ? { heroButtonGlowColor: styledBtn.glowColor } : {}),
                                                    ...(styledBtn.variant !== undefined ? { heroButtonVariant: styledBtn.variant } : {})
                                                });
                                            }}
                                            btnStyleBase={btnStyleBase}
                                            getBtnColor={_getBtnColor}
                                            normalizeAppUrl={normalizeAppUrl}
                                            buttonsAlign={hero.buttonsAlign || 'inherit'}
                                            buttonsDirection={hero.buttonsDirection || hero.buttonsLayout || 'row'}
                                            onButtonsAlignChange={(val) => update([...pathPrefix, 'buttonsAlign'], val)}
                                        />
                                    ))}
                                    {isEditing && (
                                        <button
                                            type="button"
                                            onClick={() => update([...pathPrefix, 'buttons'], [...(hero.buttons || []), { text: "Learn More", link: "#", variant: "primary" }])}
                                            style={{
                                                padding: '8px 14px',
                                                background: '#3b82f6',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            ➕ Add Hero Button
                                        </button>
                                    )}
                                </div>
                            )}
                            {includeImage && hero.imagePosition === 'bottom' && renderImage()}
                        </div>
                    );

                    if (layoutMode === 'split-left') {
                        return (
                            <div className="hero-split-layout" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '36px', width: '100%' }}>
                                <div style={{ flex: '1 1 480px', minWidth: '280px' }}>
                                    {renderContent(false)}
                                </div>
                                <div style={{ flex: '1 1 360px', minWidth: '280px', display: 'flex', justifyContent: (heroAlign === 'left' ? 'flex-end' : (heroAlign === 'right' ? 'flex-start' : 'center')), alignItems: 'center' }}>
                                    {renderImage()}
                                </div>
                            </div>
                        );
                    } else if (layoutMode === 'split-right') {
                        return (
                            <div className="hero-split-layout" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '36px', width: '100%' }}>
                                <div style={{ flex: '1 1 360px', minWidth: '280px', display: 'flex', justifyContent: (heroAlign === 'left' ? 'flex-start' : (heroAlign === 'right' ? 'flex-end' : 'center')), alignItems: 'center' }}>
                                    {renderImage()}
                                </div>
                                <div style={{ flex: '1 1 480px', minWidth: '280px' }}>
                                    {renderContent(false)}
                                </div>
                            </div>
                        );
                    } else if (layoutMode === 'banner') {
                        return (
                            <div className={`hero-split-layout ${hasCard ? 'hero-content-card' : ''}`.trim()} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '24px', width: '100%', ...(hasCard ? cardStyle : {}) }}>
                                <div style={{ flex: '1 1 500px', minWidth: '280px', textAlign: heroAlign }}>
                                    {renderBadge()}
                                    {renderImage()}
                                    <Editable
                                        as="h1"
                                        className="hero-title"
                                        isEditing={isEditing}
                                        value={hero.title !== undefined && hero.title !== null ? hero.title : ''}
                                        onChange={e => update([...pathPrefix, 'title'], e.target.value)}
                                        onStyleChange={handleTitleStyleChange}
                                        currentStyles={{ align: heroAlign, size: hero.titleSize, color: hero.titleColor, fontFamily: hero.titleFontFamily, preset: hero.titlePreset, fontWeight: hero.titleFontWeight || hero.titleWeight || hero.fontWeight || hero.weight, weight: hero.titleFontWeight || hero.titleWeight || hero.fontWeight || hero.weight, fontStyle: hero.titleFontStyle, textDecoration: hero.titleTextDecoration, textTransform: hero.titleTextTransform || hero.titleTransform, letterSpacing: hero.titleLetterSpacing, lineHeight: hero.titleLineHeight, textStrokeWidth: hero.titleTextStrokeWidth, textStrokeColor: hero.titleTextStrokeColor, textShadow: (hero.titleTextShadow && hero.titleTextShadow !== 'none') ? hero.titleTextShadow : undefined, textShadowPreset: hero.titleTextShadowPreset || hero.textShadowPreset || 'none', textShadowCustom: hero.titleTextShadowCustom || hero.textShadowCustom || '', textGradientMode: hero.titleTextGradientMode, textGradientStart: hero.titleTextGradientStart, textGradientEnd: hero.titleTextGradientEnd, textGradientAngle: hero.titleTextGradientAngle }}
                                        style={{ ...getHeroTitleStylesLocal(heroAlign), marginBottom: '8px' }}
                                    />
                                    {Boolean(hero.subtitle || isEditing) && (
                                        <Editable
                                            as="p"
                                            className="hero-subtitle"
                                            isEditing={isEditing}
                                            value={hero.subtitle !== undefined && hero.subtitle !== null ? hero.subtitle : ''}
                                            onChange={e => update([...pathPrefix, 'subtitle'], e.target.value)}
                                            onStyleChange={handleSubtitleStyleChange}
                                            currentStyles={{ align: heroSubAlign, size: hero.subtitleSize, color: hero.subtitleColor, fontFamily: hero.subtitleFontFamily, preset: hero.subtitlePreset, fontWeight: hero.subtitleFontWeight || hero.subtitleWeight || hero.subFontWeight || hero.subWeight, weight: hero.subtitleFontWeight || hero.subtitleWeight || hero.subFontWeight || hero.subWeight, fontStyle: hero.subtitleFontStyle, textDecoration: hero.subtitleTextDecoration, textTransform: hero.subtitleTextTransform || hero.subtitleTransform, letterSpacing: hero.subtitleLetterSpacing, lineHeight: hero.subtitleLineHeight, textStrokeWidth: hero.subtitleTextStrokeWidth, textStrokeColor: hero.subtitleTextStrokeColor, textShadow: (hero.subtitleTextShadow && hero.subtitleTextShadow !== 'none') ? hero.subtitleTextShadow : undefined, textShadowPreset: hero.subtitleTextShadowPreset || hero.textShadowPreset || 'none', textShadowCustom: hero.subtitleTextShadowCustom || hero.textShadowCustom || '', textGradientMode: hero.subtitleTextGradientMode, textGradientStart: hero.subtitleTextGradientStart, textGradientEnd: hero.subtitleTextGradientEnd, textGradientAngle: hero.subtitleTextGradientAngle }}
                                            style={getHeroSubtitleStylesLocal(heroSubAlign)}
                                        />
                                    )}
                                </div>
                                <div className="hero-buttons-container" style={{ display: 'flex', flexDirection: isButtonsColumn ? 'column' : 'row', gap: addUnit(hero.buttonsGap || '15px'), alignItems: isButtonsColumn ? 'flex-start' : 'center', flexWrap: 'wrap', width: isButtonsColumn ? '100%' : 'auto', maxWidth: '100%' }}>
                                    {(hero.buttons || []).map((btn, i) => (
                                        <EditableButton
                                            key={i}
                                            button={btn}
                                            isEditing={isEditing}
                                            onChange={(newBtn) => update([...pathPrefix, 'buttons', i], newBtn)}
                                            onDelete={() => {
                                                const newButtons = hero.buttons.filter((_, idx) => idx !== i);
                                                update([...pathPrefix, 'buttons'], newButtons);
                                            }}
                                            btnStyleBase={btnStyleBase}
                                            getBtnColor={_getBtnColor}
                                            normalizeAppUrl={normalizeAppUrl}
                                            buttonsAlign={hero.buttonsAlign || 'inherit'}
                                            buttonsDirection={hero.buttonsDirection || hero.buttonsLayout || 'row'}
                                            onButtonsAlignChange={(val) => update([...pathPrefix, 'buttonsAlign'], val)}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    } else if (layoutMode === 'center-split') {
                        return (
                            <div className={`hero-content-inner ${hasCard ? 'hero-content-card' : ''}`.trim()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '28px', ...(hasCard ? cardStyle : {}) }}>
                                {renderBadge()}
                                <Editable
                                    as="h1"
                                    className="hero-title"
                                    isEditing={isEditing}
                                    value={hero.title !== undefined && hero.title !== null ? hero.title : ''}
                                    onChange={e => update([...pathPrefix, 'title'], e.target.value)}
                                    onStyleChange={handleTitleStyleChange}
                                    currentStyles={{ align: 'center', size: hero.titleSize, color: hero.titleColor, fontFamily: hero.titleFontFamily, preset: hero.titlePreset, fontWeight: hero.titleFontWeight || hero.titleWeight || hero.fontWeight || hero.weight, weight: hero.titleFontWeight || hero.titleWeight || hero.fontWeight || hero.weight, fontStyle: hero.titleFontStyle, textDecoration: hero.titleTextDecoration, textTransform: hero.titleTextTransform || hero.titleTransform, letterSpacing: hero.titleLetterSpacing, lineHeight: hero.titleLineHeight, textStrokeWidth: hero.titleTextStrokeWidth, textStrokeColor: hero.titleTextStrokeColor, textShadow: (hero.titleTextShadow && hero.titleTextShadow !== 'none') ? hero.titleTextShadow : undefined, textShadowPreset: hero.titleTextShadowPreset || hero.textShadowPreset || 'none', textShadowCustom: hero.titleTextShadowCustom || hero.textShadowCustom || '', textGradientMode: hero.titleTextGradientMode, textGradientStart: hero.titleTextGradientStart, textGradientEnd: hero.titleTextGradientEnd, textGradientAngle: hero.titleTextGradientAngle }}
                                    style={getHeroTitleStylesLocal('center')}
                                />
                                {Boolean(hero.subtitle || isEditing) && (
                                    <Editable
                                        as="p"
                                        className="hero-subtitle"
                                        isEditing={isEditing}
                                        value={hero.subtitle !== undefined && hero.subtitle !== null ? hero.subtitle : ''}
                                        onChange={e => update([...pathPrefix, 'subtitle'], e.target.value)}
                                        onStyleChange={handleSubtitleStyleChange}
                                        currentStyles={{ align: heroSubAlign, size: hero.subtitleSize, color: hero.subtitleColor, fontFamily: hero.subtitleFontFamily, preset: hero.subtitlePreset, fontWeight: hero.subtitleFontWeight || hero.subtitleWeight || hero.subFontWeight || hero.subWeight, weight: hero.subtitleFontWeight || hero.subtitleWeight || hero.subFontWeight || hero.subWeight, fontStyle: hero.subtitleFontStyle, textDecoration: hero.subtitleTextDecoration, textTransform: hero.subtitleTextTransform || hero.subtitleTransform, letterSpacing: hero.subtitleLetterSpacing, lineHeight: hero.subtitleLineHeight, textStrokeWidth: hero.subtitleTextStrokeWidth, textStrokeColor: hero.subtitleTextStrokeColor, textShadow: (hero.subtitleTextShadow && hero.subtitleTextShadow !== 'none') ? hero.subtitleTextShadow : undefined, textShadowPreset: hero.subtitleTextShadowPreset || hero.textShadowPreset || 'none', textShadowCustom: hero.subtitleTextShadowCustom || hero.textShadowCustom || '', textGradientMode: hero.subtitleTextGradientMode, textGradientStart: hero.subtitleTextGradientStart, textGradientEnd: hero.subtitleTextGradientEnd, textGradientAngle: hero.subtitleTextGradientAngle }}
                                        style={getHeroSubtitleStylesLocal(heroSubAlign)}
                                    />
                                )}
                                {renderImage()}
                                {((hero.buttons && hero.buttons.length > 0) || isEditing) && (
                                    <div className="hero-buttons-container" style={{ display: 'flex', flexDirection: isButtonsColumn ? 'column' : 'row', gap: addUnit(hero.buttonsGap || '15px'), justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center', width: isButtonsColumn ? '100%' : 'auto', maxWidth: '100%' }}>
                                        {(hero.buttons || []).map((btn, i) => (
                                            <EditableButton
                                                key={i}
                                                button={btn}
                                                isEditing={isEditing}
                                                onChange={(newBtn) => update([...pathPrefix, 'buttons', i], newBtn)}
                                                onDelete={() => {
                                                    const newButtons = hero.buttons.filter((_, idx) => idx !== i);
                                                    update([...pathPrefix, 'buttons'], newButtons);
                                                }}
                                                btnStyleBase={btnStyleBase}
                                                getBtnColor={_getBtnColor}
                                                normalizeAppUrl={normalizeAppUrl}
                                                buttonsAlign="center"
                                                buttonsDirection={hero.buttonsDirection || hero.buttonsLayout || 'row'}
                                                onButtonsAlignChange={(val) => update([...pathPrefix, 'buttonsAlign'], val)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    }

                    // Default: Stack
                    return renderContent(true);
                })()}
            </div>

            {renderHeroBottomDivider(hero, settings)}
        </header>
    );
};

export default HeroBlock;
